import { AlertCircle, Loader2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { FilePicker } from "./FilePicker";

export interface UploadModalProps {
  open: boolean;
  onClose: () => void;
  onUpload: (files: File[]) => void;
  isUploading: boolean;
  error?: string | null;
}

export function UploadModal({ open, onClose, onUpload, isUploading, error }: Readonly<UploadModalProps>) {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [rejectedFiles, setRejectedFiles] = useState<string[]>([]);

  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (open) return;
    setSelectedFiles([]);
    setRejectedFiles([]);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onCloseRef.current();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [open]);

  if (!open) return null;

  const handleFilesChange = (accepted: File[], rejected: string[]) => {
    setSelectedFiles(accepted);
    setRejectedFiles(rejected);
  };

  const handleUpload = () => {
    if (selectedFiles.length === 0) return;
    onUpload(selectedFiles);
  };

  // Prevent the browser's default behavior (navigating to / "downloading" the
  // file) when a file is dropped anywhere within the modal but outside the
  // dropzone.
  const preventDefaultDrop = (e: React.DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 bg-surface-overlay backdrop-blur-xs flex items-center justify-center z-50 p-4"
      onDragOver={preventDefaultDrop}
      onDrop={preventDefaultDrop}
    >
      <div className="bg-surface-primary border border-border-primary rounded-xl w-full max-w-lg max-h-[calc(100dvh-2rem)] flex flex-col">
        <div className="flex shrink-0 items-center justify-between p-4 border-b border-border-primary">
          <h2 className="text-lg font-semibold text-content-primary">Upload Files</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="p-1 text-content-tertiary hover:text-content-primary transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
          {error && (
            <div
              role="alert"
              className="flex items-start gap-1.5 bg-red-500/10 border border-red-500/20 rounded-lg p-3 text-sm text-red-400"
            >
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
          <FilePicker selectedFiles={selectedFiles} rejectedFiles={rejectedFiles} onChange={handleFilesChange} />
        </div>
        <div className="flex shrink-0 items-center justify-end gap-3 p-4 border-t border-border-primary">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-content-secondary hover:text-content-primary transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleUpload}
            disabled={isUploading || selectedFiles.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUploading && <Loader2 className="h-4 w-4 animate-spin" />}
            {isUploading ? "Uploading..." : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
}
